import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/billingPaymentTrack/paymentTypeFormStyle';
import { BodyS, HeadingSBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import { INVOICE_TYPE } from 'views/BillingPaymentTrack/commons/constants';
import { BillingPaymentTrackService } from 'services/billingPaymentTrack/billingPaymentTrack';
import { BankItem } from 'utils/api/models/billingPaymentTrack/bankItem';
import { finalize } from 'rxjs/operators';
import { getLanguage } from 'utils/language/language';
import { FormControl, FormLabel, IconButton, MenuItem, Select, TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';

// @ts-ignore
const useStyles = makeStyles(styles);

type IPaymentTypeFormProps = {
	invoiceType: string;
	setInvoiceType: React.Dispatch<React.SetStateAction<string>>;
	bankCode: string;
	setBankCode: React.Dispatch<React.SetStateAction<string>>;
	bankAccountNumber: string;
	setBankAccountNumber: React.Dispatch<React.SetStateAction<string>>;
	onClickBack: () => void;
};

const PaymentTypeForm = ({
	invoiceType,
	setInvoiceType,
	setBankCode,
	bankAccountNumber,
	setBankAccountNumber,
	onClickBack,
	bankCode,
}: IPaymentTypeFormProps) => {
	const classes = useStyles();
	const [bankItems, setBankItems] = useState<BankItem[]>([]);
	const lang = getLanguage();

	useEffect(() => {
		const subscription = BillingPaymentTrackService.getBanks(lang).subscribe((resp) =>
			setBankItems(resp.bankItems)
		);

		return () => {
			subscription.unsubscribe();
		};
	}, [lang]);

	const bankItemsWithLabels = bankItems.map((bankItem) => {
		return { name: bankItem.name, id: bankItem.code };
	});

	const onBankAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		setBankAccountNumber(value);
	};

	const onBankSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
		setBankCode(event.target.value as string);
	};

	return (
		<div className={classes.smallTab}>
			<div className={classes.tab}>
				<div className={classes.paymentTypeFormTitleWrapper}>
					<HeadingSBold disableMargin>
						{translate('billing.payment.track.payment.type.form.title')}
					</HeadingSBold>
					<IconButton size="small" onClick={onClickBack}>
						<Close />
					</IconButton>
				</div>
				<div className={classes.radioButtonsContainer}>
					<div className={classes.radioButtons}>
						<BodyS>{translate('billing.payment.track.payment.type.form.invoice.type')}</BodyS>
						<div className={classes.radioButtonsList}>
							<CustomRadio
								checked={invoiceType === INVOICE_TYPE.ACCOUNT}
								onChange={() => setInvoiceType(INVOICE_TYPE.ACCOUNT)}
								label={translate('billing.payment.track.payment.type.form.invoice.type.account')}
							/>
							<CustomRadio
								checked={invoiceType === INVOICE_TYPE.INVOICE}
								onChange={() => setInvoiceType(INVOICE_TYPE.INVOICE)}
								label={translate('billing.payment.track.payment.type.form.invoice.type.invoice')}
							/>
						</div>
					</div>
				</div>
				<div className={classes.inputsFormContainer}>
					<div className={classes.input}>
						<FormLabel className={classes.inputLabel}>
							{translate('billing.payment.track.status.bank.name')}
						</FormLabel>
						<FormControl variant="outlined" fullWidth>
							<Select
								value={bankCode}
								onChange={onBankSelect}
								placeholder={translate('billing.payment.track.payment.details.form.bank.name.label')}
							>
								{bankItemsWithLabels.map((bank) => (
									<MenuItem key={bank.id} value={bank.id}>
										{bank.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
					<div className={classes.input}>
						<FormLabel className={classes.inputLabel}>
							{translate('billing.payment.track.payment.details.form.bank.account.number.label')}
						</FormLabel>
						<FormControl fullWidth>
							<TextField
								required
								type="number"
								variant="outlined"
								id="bankAccountNumber"
								value={bankAccountNumber}
								onChange={onBankAccountNumberChange}
								className={classes.input}
								inputProps={{ maxLength: 50, 'aria-autocomplete': 'none', min: 0 }}
							/>
						</FormControl>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaymentTypeForm;
